import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiMapPin, FiMail, FiBookOpen, FiShield, FiCheck, FiCookie } = FiIcons;

const Section = ({ id, children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  return (
    <motion.section
      ref={ref}
      id={id}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 50 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className={`py-12 ${className}`}
    >
      {children}
    </motion.section>
  );
};

const PolicyListItem = ({ children }) => (
  <li className="flex items-start mt-2">
    <SafeIcon icon={FiCheck} className="flex-shrink-0 w-5 h-5 mt-1 mr-2 text-purple-600" />
    <p className="text-gray-700">{children}</p>
  </li>
);

const renderPolicyText = (text) => {
  const paragraphs = text.split(/\n\n|\n(?=[●\d])/).filter(p => p.trim() !== '');

  return paragraphs.map((paragraph, index) => {
    const trimmed = paragraph.trim();
    
    if (trimmed.startsWith('●')) {
      const bullets = trimmed.split('\n').map(b => b.trim().replace(/^●\s*/, '')).filter(b => b);
      return (
        <ul key={index} className="list-disc ml-6 space-y-2 mb-4">
          {bullets.map((bullet, bIndex) => (
            <li key={bIndex} className="text-gray-700">{bullet}</li>
          ))}
        </ul>
      );
    } else if (trimmed.startsWith('Note:') || trimmed.startsWith('Warning:')) {
      return (
        <div key={index} className="px-4 py-3 my-4 border-l-4 border-yellow-500 bg-yellow-50 text-yellow-800 rounded-md">
          <p className="font-medium">{trimmed}</p>
        </div>
      );
    } else if (trimmed.startsWith('Important:')) {
      return (
        <div key={index} className="px-4 py-3 my-4 border-l-4 border-purple-500 bg-purple-50 text-purple-800 rounded-md">
          <p className="font-medium">{trimmed}</p>
        </div>
      );
    } else if (/^\d+\./.test(trimmed)) {
      const items = trimmed.split('\n').filter(i => /^\d+\./.test(i.trim()));
      return (
        <ol key={index} className="list-decimal ml-6 space-y-2 mb-4">
          {items.map((item, iIndex) => (
            <li key={iIndex} className="text-gray-700">{item.replace(/^\d+\.\s*/, '')}</li>
          ))}
        </ol>
      );
    }
    
    return <p key={index} className="text-gray-700 leading-relaxed mb-4">{trimmed}</p>;
  });
};

const CookieTable = ({ data, columns }) => (
  <div className="overflow-x-auto shadow-lg rounded-xl border border-gray-200 my-4">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-purple-100">
        <tr>
          {columns.map((col, idx) => (
            <th key={idx} className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {data.map((row, index) => (
          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
            {Object.values(row).map((cell, cellIdx) => (
              <td key={cellIdx} className="px-4 py-3 whitespace-normal text-sm text-gray-700">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CookiePolicy = () => {
  const policyContent = [
    {
      title: 'ABOUT THIS COOKIE POLICY',
      id: 'about-cookie-policy',
      sections: [
        {
          subtitle: 'Introduction',
          content: `This Cookie Policy explains how TOGETHR SPACE LTD ("we", "us", "our") uses cookies and similar technologies on our website www.togethr.space and mobile application (together, the "Platform").

This policy should be read together with our Privacy Policy, which provides more information about how we collect, use, and protect your personal data.`,
        },
        {
          subtitle: 'Company Details',
          content: `Company Name: TOGETHR SPACE LTD
Company Number: 13917780
Registered Office: 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ, United Kingdom
Contact Email: hi@togethr.space`,
        },
        {
          subtitle: 'Regulatory Compliance',
          content: `We comply with:
● The UK General Data Protection Regulation (UK GDPR)
● The Data Protection Act 2018 (DPA 2018)
● The Privacy and Electronic Communications Regulations 2003 (PECR)`,
        },
        {
          subtitle: 'Updates to This Policy',
          content: `We may update this Cookie Policy to reflect changes in technology, law, or our practices. When we make material changes, we will:
● Update the "Last updated" date at the top
● Notify you through our Platform or by email
● Re-request your consent for non-essential cookies if necessary

We recommend checking this policy periodically.`,
        },
      ],
    },
    {
      title: 'CONTACT INFORMATION',
      id: 'contact-info',
      sections: [
        {
          content: `If you have any questions about our use of cookies, please contact:

Data Protection Contact: Kevin Leonce
Email: hi@togethr.space`,
        },
      ],
    },
    {
      title: 'WHAT ARE COOKIES?',
      id: 'what-are-cookies',
      sections: [
        {
          subtitle: 'Definition',
          content: `Cookies are small text files that are placed on your device (computer, smartphone, tablet) when you visit our Platform. They are widely used to make websites and apps work efficiently and provide information to the owners.`,
        },
        {
          subtitle: 'How Cookies Work',
          content: `Cookies contain small amounts of information that are downloaded to your device. They are then sent back to the originating website or app on each subsequent visit, or to another website/app that recognises that cookie. This allows the service to recognise your device and remember information about your visit.`,
        },
        {
          subtitle: 'Similar Technologies',
          content: `This policy also covers similar technologies that store or access information on your device, including:
● Web beacons / pixel tags: Small graphic images that track page views and email opens
● Local storage: HTML5 local storage for storing data in your browser
● SDKs (Software Development Kits): In mobile apps, for analytics and functionality
● Device fingerprinting: Collecting device configuration information

Throughout this policy, we use "cookies" to refer to cookies and all similar technologies.`,
        },
      ],
    },
    {
      title: 'WHY WE USE COOKIES',
      id: 'why-cookies',
      sections: [
        {
          content: `We use cookies for several important purposes:
● Essential functionality: Making the Platform work properly
● Security: Protecting your account and detecting fraud
● Performance: Understanding how you use the Platform so we can improve it
● Personalisation: Remembering your preferences and customising your experience
● Analytics: Measuring visitor traffic and behaviour
● Advertising: Delivering relevant advertisements (only with your consent)

Important: We will never use cookies to:
● Track your sexual orientation or gender identity without your explicit consent
● Share sensitive information about you with third parties for their own purposes
● "Out" you to others without your permission`,
        },
      ],
    },
    {
      title: 'TYPES OF COOKIES WE USE',
      id: 'cookie-types',
      sections: [
        {
          subtitle: 'By Lifespan',
          content: `Session Cookies (Temporary)
● Deleted when you close your browser
● Essential for the Platform to function during your visit
● Example: Keeping you logged in as you navigate between pages

Persistent Cookies (Stored)
● Remain on your device for a set period or until you delete them
● Remember your preferences for future visits
● Example: Remembering your language preference`,
        },
        {
          subtitle: 'By Origin',
          content: `First-Party Cookies
● Set by us (TOGETHR SPACE LTD)
● Used to operate the Platform and remember your preferences

Third-Party Cookies
● Set by other companies (e.g., Google Analytics, advertising networks)
● Used for analytics, advertising, and social media features
● Subject to the third parties' privacy policies`,
        },
      ],
    },
    {
      title: 'STRICTLY NECESSARY COOKIES',
      id: 'necessary-cookies',
      sections: [
        {
          subtitle: 'What They Do',
          content: `These cookies are essential for the Platform to function and cannot be switched off. They are usually set in response to actions you take, such as:
● Logging into your account
● Setting your privacy preferences
● Filling in forms
● Adding items to a basket

Without these cookies, services you have requested cannot be provided.`,
        },
        {
          subtitle: 'Legal Basis',
          content: `No consent required under PECR Regulation 6(4), as these cookies are "strictly necessary" for services you have explicitly requested.`,
        },
        {
          subtitle: 'Specific Cookies We Use',
          type: 'table',
          tableData: [
            { name: 'togethr_session', purpose: 'Maintains your login session', duration: 'Session (deleted when browser closes)', setBy: 'togethr.space' },
            { name: 'csrf_token', purpose: 'Protects against cross-site request forgery attacks', duration: 'Session', setBy: 'togethr.space' },
            { name: 'cookie_consent', purpose: 'Remembers your cookie preferences', duration: '12 months', setBy: 'togethr.space' },
            { name: 'load_balancer', purpose: 'Distributes traffic across servers for stability', duration: 'Session', setBy: 'togethr.space' },
          ],
          columns: ['Cookie Name', 'Purpose', 'Duration', 'Set By'],
        },
      ],
    },
    {
      title: 'PERFORMANCE & ANALYTICS COOKIES',
      id: 'analytics-cookies',
      sections: [
        {
          subtitle: 'What They Do',
          content: `These cookies collect information about how you use our Platform:
● Which pages you visit
● How long you spend on each page
● Links you click
● Error messages you encounter

This helps us understand user behaviour and identify areas for improvement. All information collected is aggregated and anonymised.`,
        },
        {
          subtitle: 'Legal Basis',
          content: `Consent required under PECR Regulation 6.`,
        },
        {
          subtitle: 'Specific Cookies We Use',
          type: 'table',
          tableData: [
            { name: '_ga', purpose: 'Distinguishes unique users for Google Analytics', duration: '2 years', setBy: 'Google Analytics', info: 'Google Privacy Policy' },
            { name: '_ga_*', purpose: 'Maintains session state for Google Analytics 4', duration: '2 years', setBy: 'Google Analytics', info: 'Google Privacy Policy' },
            { name: '_gid', purpose: 'Distinguishes unique users', duration: '24 hours', setBy: 'Google Analytics', info: 'Google Privacy Policy' },
            { name: 'togethr_analytics', purpose: 'Tracks feature usage for internal analytics', duration: '90 days', setBy: 'togethr.space', info: 'N/A' },
          ],
          columns: ['Cookie Name', 'Purpose', 'Duration', 'Set By', 'More Info'],
        },
        {
          subtitle: 'Third-Party Analytics',
          content: `We use Google Analytics with the following privacy settings:
● IP Anonymisation enabled: Your IP address is truncated before processing
● Data sharing disabled: We do not share data with Google for their own purposes
● User-ID tracking disabled: We do not send identifiable user information

You can opt out of Google Analytics across all websites by installing the Google Analytics Opt-out Browser Add-on.`,
        },
      ],
    },
    {
      title: 'FUNCTIONALITY & CUSTOMISATION COOKIES',
      id: 'functionality-cookies',
      sections: [
        {
          subtitle: 'What They Do',
          content: `These cookies enable enhanced functionality and personalisation:
● Remembering your username (not password)
● Storing your language preference
● Remembering accessibility settings (font size, contrast)
● Saving your privacy settings (e.g., who can see your posts)
● Remembering items you viewed or placed in a basket

These cookies cannot track your browsing activity on other websites.`,
        },
        {
          subtitle: 'Legal Basis',
          content: `Consent required under PECR Regulation 6 (though some may be considered strictly necessary depending on usage).`,
        },
        {
          subtitle: 'Specific Cookies We Use',
          type: 'table',
          tableData: [
            { name: 'preferred_name', purpose: 'Remembers the name you prefer to be called', duration: '12 months', setBy: 'togethr.space' },
            { name: 'language_pref', purpose: 'Stores your language preference', duration: '12 months', setBy: 'togethr.space' },
            { name: 'accessibility', purpose: 'Remembers your accessibility settings', duration: '12 months', setBy: 'togethr.space' },
            { name: 'theme', purpose: 'Remembers light/dark mode preference', duration: '12 months', setBy: 'togethr.space' },
            { name: 'saved_items', purpose: "Stores items you've bookmarked or saved", duration: '30 days', setBy: 'togethr.space' },
          ],
          columns: ['Cookie Name', 'Purpose', 'Duration', 'Set By'],
        },
      ],
    },
    {
      title: 'SOCIAL MEDIA COOKIES',
      id: 'social-cookies',
      sections: [
        {
          subtitle: 'What They Do',
          content: `These cookies are set by social media platforms when you use their features on our Platform, such as:
● "Share" or "Like" buttons (Facebook, Instagram, WhatsApp)
● Embedded social media posts or videos
● Social login (e.g., "Sign in with Facebook")

These cookies may track your browsing activity across different websites.`,
        },
        {
          subtitle: 'Privacy Note',
          content: `We do not control these cookies. They are subject to the respective social media platform's privacy policy. We recommend reviewing their policies before using these features.

Important for LGBTQIA+ Users: Sharing content on social media may reveal your use of our Platform. Consider your privacy before using these features.`,
        },
      ],
    },
    {
      title: 'TARGETING & ADVERTISING COOKIES',
      id: 'advertising-cookies',
      sections: [
        {
          subtitle: 'Current Status',
          content: `We do not currently use targeting or advertising cookies. If we introduce them in the future, we will:
● Update this Cookie Policy
● Seek your renewed consent through our cookie banner
● Provide clear opt-out options`,
        },
      ],
    },
    {
      title: 'HOW TO MANAGE YOUR COOKIE PREFERENCES',
      id: 'manage-cookies',
      sections: [
        {
          subtitle: 'Our Cookie Consent Tool',
          content: `When you first visit our Platform, you will see a cookie banner that allows you to:
● Accept all cookies: Including optional cookies
● Reject non-essential cookies: Only strictly necessary cookies will be set
● Customise preferences: Choose which categories of cookies to allow

You can change your preferences at any time by:
1. Clicking the "Cookie Settings" link in the footer of our website
2. Accessing "Privacy Settings" in your account (if logged in)
3. Clearing your browser cookies and revisiting the Platform to see the banner again`,
        },
        {
          subtitle: 'Browser Settings',
          content: `You can also control cookies through your browser settings. Most browsers allow you to:
● Block all cookies
● Block third-party cookies only
● Delete existing cookies
● Set cookie preferences for specific websites

How to Access Cookie Settings:
● Chrome: Settings > Privacy and security > Cookies and other site data
● Firefox: Settings > Privacy & Security > Cookies and Site Data
● Safari: Preferences > Privacy > Cookies and website data
● Edge: Settings > Cookies and site permissions

Warning: Blocking all cookies may prevent parts of our Platform from working properly.

For more information, visit www.allaboutcookies.org.`,
        },
        {
          subtitle: 'Mobile App Settings',
          content: `In our mobile app, you can manage cookies and similar technologies through:
● App Settings > Privacy > Cookie Preferences
● Device Settings: iOS (Settings > Privacy) or Android (Settings > Privacy > Ads)`,
        },
      ],
    },
    {
      title: 'CONSENT REQUIREMENTS',
      id: 'consent',
      sections: [
        {
          subtitle: 'When We Need Your Consent',
          content: `Under UK law (PECR), we must obtain your consent before setting non-essential cookies. This includes:
● Analytics cookies
● Functionality cookies (in most cases)
● Social media cookies
● Advertising cookies

We do not need consent for strictly necessary cookies.`,
        },
        {
          subtitle: 'What Valid Consent Looks Like',
          content: `Your consent must be:
● Freely given: You have a genuine choice and can refuse without detriment
● Specific: Separate consent for different cookie categories
● Informed: You understand what you're consenting to (this policy provides that information)
● Unambiguous: Given through a clear affirmative action (e.g., clicking "Accept")
● Easily withdrawable: You can change your mind at any time`,
        },
      ],
    },
    {
      title: 'SPECIAL CONSIDERATIONS FOR LGBTQIA+ USERS',
      id: 'lgbtq-considerations',
      sections: [
        {
          subtitle: 'Privacy Sensitivity',
          content: `We recognise that cookies can reveal sensitive information about your use of our Platform, which could inadvertently "out" you to others who have access to your device.`,
        },
        {
          subtitle: 'Minimising Risks',
          content: `To protect your privacy:
● Use a private/incognito browsing session when accessing our Platform on shared devices
● Log out after each session (this will delete session cookies)
● Clear your browser cookies after using our Platform if privacy is a concern
● Consider disabling all non-essential cookies`,
        },
        {
          subtitle: "What We Don't Do",
          content: `We never:
● Store your sexual orientation or gender identity in cookies
● Share cookie data that could identify you as LGBTQIA+ with third parties without your explicit permission`,
        },
      ],
    },
    {
      title: 'CONTACT US',
      id: 'final-contact',
      sections: [
        {
          content: `If you have any questions about our use of cookies or this Cookie Policy, please contact:

TOGETHR SPACE LTD
Data Protection Contact: Kevin Leonce
Email: hi@togethr.space`,
        },
      ],
    },
    {
      title: 'COMPLAINTS',
      id: 'complaints',
      sections: [
        {
          content: `If you are unhappy with how we use cookies or handle your personal data, you can complain to:

Information Commissioner's Office (ICO)
● Website: https://ico.org.uk/make-a-complaint/
● Phone: 0303 123 1113
● Address: Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF`,
        },
      ],
    },
    {
      title: 'EFFECTIVE DATE AND VERSION',
      id: 'effective-date',
      sections: [
        {
          content: `This Cookie Policy is effective as of: 4 December 2025
Version: 2.0

Thank you for taking the time to understand how we use cookies. Your privacy and safety matter to us.`,
        },
      ],
    },
  ];

  const tableOfContents = policyContent.map((item, index) => ({
    id: item.id,
    title: `${index + 1}. ${item.title}`,
  }));

  return (
    <div className="min-h-screen bg-white">
      <header className="py-20 bg-indigo-600 text-white shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl sm:text-5xl font-extrabold tracking-tight flex items-center"
          >
            <SafeIcon icon={FiCookie} className="w-12 h-12 mr-4" />
            Cookie Policy
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-3 text-xl text-indigo-200"
          >
            TOGETHR SPACE LTD
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-6 flex flex-wrap gap-4 text-indigo-100 text-sm"
          >
            <span className="flex items-center">
              <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2" />
              Last updated: 4 December 2025
            </span>
            <span className="flex items-center">
              <SafeIcon icon={FiShield} className="w-4 h-4 mr-2" />
              UK GDPR & PECR Compliant
            </span>
            <a href="#contact-info" className="flex items-center hover:text-white transition-colors">
              <SafeIcon icon={FiMail} className="w-4 h-4 mr-2" />
              Contact: Kevin Leonce
            </a>
          </motion.div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8 pt-12">
          <div className="lg:col-span-3 lg:sticky lg:top-12 h-fit mb-10 lg:mb-0">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="p-6 bg-gray-50 rounded-xl shadow-lg border border-gray-200"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <SafeIcon icon={FiBookOpen} className="w-5 h-5 mr-2 text-indigo-600" />
                Table of Contents
              </h3>
              <nav className="space-y-2">
                {tableOfContents.map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className="block text-sm text-gray-600 hover:text-indigo-600 transition-colors py-1 pl-2 border-l-2 border-gray-300 hover:border-indigo-600"
                  >
                    {item.title}
                  </a>
                ))}
              </nav>
            </motion.div>
          </div>

          <div className="lg:col-span-9">
            <div className="divide-y divide-gray-200">
              {policyContent.map((section, sectionIndex) => (
                <Section key={section.id} id={section.id} className="pt-10 pb-6">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 border-b-2 border-indigo-200 pb-2">
                    {sectionIndex + 1}. {section.title}
                  </h2>
                  
                  {section.sections.map((sub, subIndex) => (
                    <div key={subIndex} className="mt-6">
                      {sub.subtitle && (
                        <h3 className="text-xl font-semibold text-gray-800 mb-3">
                          {sectionIndex + 1}.{subIndex + 1} {sub.subtitle}
                        </h3>
                      )}
                      
                      {sub.type === 'table' && (
                        <CookieTable data={sub.tableData} columns={sub.columns} />
                      )}
                      {sub.content && renderPolicyText(sub.content)}
                    </div>
                  ))}
                </Section>
              ))}
            </div>

            <div className="mt-16 mb-16 p-8 bg-indigo-50 rounded-xl border border-indigo-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Manage Your Cookie Preferences</h3>
              <p className="text-gray-700 mb-4">
                You can control which cookies we use at any time through our Cookie Settings.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => {
                    const event = new CustomEvent('openCookieSettings');
                    window.dispatchEvent(event);
                  }}
                  className="inline-flex items-center justify-center px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <SafeIcon icon={FiCookie} className="w-5 h-5 mr-2" />
                  Open Cookie Settings
                </button>
                <a
                  href="mailto:hi@togethr.space"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-indigo-600 font-medium rounded-lg border-2 border-indigo-600 hover:bg-indigo-50 transition-colors"
                >
                  <SafeIcon icon={FiMail} className="w-5 h-5 mr-2" />
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;